import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { BookableStorageLocation } from '@luggagehero/shared/interfaces';
import { SharedDocumentService } from '@luggagehero/shared/services/document';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { Subscription } from 'rxjs';

import { BaseComponent } from '../../../core';
import { DateUtil } from '../../../utils/date.util';

@Component({ template: '' })
export abstract class InStoreDisplayBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  public directDropoffUrl: string;

  private _storageLocation: BookableStorageLocation;
  private subscriptions: Subscription[] = [];

  constructor(
    private storageLocationService: SharedShopsService,
    private document: SharedDocumentService,
    private window: SharedWindowService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
  ) {
    super();
  }

  get directDropoffShortUrl(): string {
    if (!this.storageLocation) {
      return null;
    }
    return AppConfig.IN_STORE_QR_SHORT_LINKS[this.storageLocation._id];
  }

  get isLuggageImageExampleEnabled(): boolean {
    if (!this.storageLocation) {
      return null;
    }
    return AppConfig.SHOPS_WITH_LUGGAGE_IMAGE_EXAMPLE_ENABLED.includes(this.storageLocation._id);
  }

  get storageLocation(): BookableStorageLocation {
    return this._storageLocation;
  }
  set storageLocation(value: BookableStorageLocation) {
    this.directDropoffUrl = `${Config.environment.TRAVELER_UI}/dropoff/${value._id}`;

    this._storageLocation = value;

    this.cd.markForCheck();
  }

  get wifiSsid(): string {
    if (!this.hasWifiInfo) {
      return null;
    }
    return this.storageLocation.wifiInfo.ssid;
  }

  get wifiUsername(): string {
    if (!this.hasWifiInfo) {
      return null;
    }
    return this.storageLocation.wifiInfo.username;
  }

  get wifiPassword(): string {
    if (!this.hasWifiInfo) {
      return null;
    }
    return this.storageLocation.wifiInfo.password;
  }

  get hasWifiInfo(): boolean {
    if (!this.storageLocation || !this.storageLocation.hasWifi || !this.storageLocation.wifiInfo) {
      return false;
    }
    const wifi = this.storageLocation.wifiInfo;
    return wifi.ssid || wifi.username || wifi.password ? true : false;
  }

  /**
   * Returns a data string containing Wi-Fi access info, which can be used to generate a QR code that should work on
   * most mobile devices.
   *
   * Specification taken from:
   * https://github.com/zxing/zxing/wiki/Barcode-Contents#wi-fi-network-config-android-ios-11
   */
  get wifiData(): string {
    if (!this.wifiSsid) {
      return null;
    }

    const elements = [`S:${this.wifiSsid}`];

    if (this.wifiPassword && !this.wifiUsername) {
      elements.push(`P:${this.wifiPassword}`);
      elements.push('T:WPA');
    }

    return `WIFI:${elements.join(';')};`;
  }

  get isSharingAvailable(): boolean {
    return this.window.isSharingAvailable;
  }

  get isFullscreenAvailable(): boolean {
    return this.document.isFullscreenAvailable;
  }

  public ngOnInit() {
    this.subscriptions.push(
      this.route.params.subscribe((params: { id: string }) => {
        void (async () => {
          const from = DateUtil.serializeDate(new Date());
          const to = DateUtil.serializeDate(new Date());
          this.storageLocation = await this.storageLocationService.getShopDetails(
            params.id,
            from,
            to,
            1,
            0,
            'marketplace',
          );
        })();
      }),
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public enterFullscreen() {
    this.document.enterFullscreen();
  }

  public exitFullscreen() {
    this.document.exitFullscreen();
  }

  public print() {
    this.window.print();
  }

  public share() {
    // TODO: Add utm params and shorten link
    const site = Config.environment.STORAGE_MANAGER_UI;
    const linkToShare = `${site}/storage-locations/${this.storageLocation._id}/in-store-display`;
    void this.window.shareLink(linkToShare, `${this.storageLocation.name}`);
  }
}
